import { useTranslation } from "react-i18next";
import { Text } from "components/text";
import { formatCurrencyCompact, formatNumber } from "utils/formatting";
import { TotalSalesFooterMarketChip } from "./TotalSalesFooterMarketChip";

export interface TotalSalesFooterProps {
  units: number;
  revenueByCurrency: { currency: string; revenue: number }[];
  byMarket: {
    market: string;
    units: number;
    revenue: number | null;
    currency: string;
  }[];
  display: "combined" | "byMarket";
  marketColorClasses?: Record<string, string>;
}

const ChipDivider = () => (
  <span className="mx-1.5 h-4 w-px shrink-0 bg-border" aria-hidden />
);

export const TotalSalesFooter = ({
  units,
  revenueByCurrency,
  byMarket,
  display,
  marketColorClasses = {},
}: TotalSalesFooterProps) => {
  const { t } = useTranslation(["productDetails"]);

  if (display === "byMarket") {
    const revenueMarkets = byMarket.filter((market) => market.revenue != null);
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-y-1">
          {revenueMarkets.map((market, index) => (
            <span key={market.market} className="inline-flex items-center">
              {index > 0 && <ChipDivider />}
              <TotalSalesFooterMarketChip
                market={market.market}
                className={marketColorClasses[market.market]}
                value={formatCurrencyCompact(
                  market.revenue as number,
                  market.currency,
                )}
              />
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-y-1">
          <Text as="span" variant="metricCaption" className="mr-1.5 uppercase">
            {t("productDetails:salesData.totalSales.footer.units")}
          </Text>
          {byMarket.map((market, index) => (
            <span key={market.market} className="inline-flex items-center">
              {index > 0 && <ChipDivider />}
              <TotalSalesFooterMarketChip
                market={market.market}
                className={marketColorClasses[market.market]}
                value={formatNumber(market.units)}
              />
            </span>
          ))}
        </div>
      </div>
    );
  }

  const revenue = revenueByCurrency
    .map((entry) => formatCurrencyCompact(entry.revenue, entry.currency))
    .join(" · ");

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <Text as="span" variant="metric">
        {t("productDetails:salesData.totalSales.footer.totalSales")}{" "}
        <span className="font-semibold text-foreground">{revenue}</span>
      </Text>
      <Text as="span" variant="metric">
        {t("productDetails:salesData.totalSales.footer.totalUnits")}{" "}
        <span className="font-semibold tabular-nums text-foreground">
          {formatNumber(units)}
        </span>
      </Text>
    </div>
  );
};
